import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '../../../../../components/ui/popover';
import { useEnumValues } from '../../hooks/useEnumValues';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import { openEnumDetailsDialog } from '../meta-info/enumDetailsDialogStore';
import { useLookupEnumLabelColumn } from '../filters/useLookupEnumLabelColumn';
import { resolveEffectiveLookupDeclaration } from '../../utils/effectiveLookupDeclaration';
import { EnumCellEditorFooter } from './EnumCellEditorFooter';
import { CellFkRowPicker } from './CellFkRowPicker';
import { useAnalytics } from '../../../../../core/analytics';
import type { CellEditFkTarget } from './cellEditVariant';
import type { FkPickerTarget } from './FkCellEditor';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { EnumLookupTarget, LooseRelationship } from '../../../../../types/electron';
import type { TableExplorerRelationshipPort } from '../../../table-explorer/components/tableExplorerHostPorts';
import { cn } from '../../../../../lib/utils';

export type EnumCellEditorProps = {
  initialValue: unknown;
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: string;
  /** 'declared' when the column has a MetaInfo declaration; 'native' when it's a native enum type. Drives gear visibility + analytics. */
  source: 'declared' | 'native';
  /** When true, the picker offers a NULL row and Ctrl+0 commits null. Defaults to true. */
  nullable?: boolean;
  /**
   * FK target this column ALSO points at (the declared enum outranked it —
   * see `cellEditVariant.ts`). Present ⇒ the footer offers the row-picker
   * escape into `FkLookupPicker`.
   */
  fkTarget?: CellEditFkTarget;
  /** Loose declarations behind `fkTarget`; the first drives the picker's scope. */
  looseMatches?: readonly LooseRelationship[];
  /** Source column's dataType — the row picker's source context. */
  dataType?: string;
  /**
   * Host port (Azure Table, S4 review C, M7): the label column and the
   * "Search rows" escape read through it, never through SQL. Absent = SQL.
   */
  relationshipPort?: TableExplorerRelationshipPort;
  /** The host refused the last commit (S4 review G, G5). SQL never sets it: its input is unchanged. */
  invalid?: boolean;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  /** Tailwind classes for the inner <input> — caller controls sizing. */
  inputClassName?: string;
  /** Tailwind classes for the outer wrapper. */
  containerClassName?: string;
};

function formatForEdit(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parseEditedValue(text: string, originalValue: unknown): unknown {
  if (text === '') return null;
  if (typeof originalValue === 'number') {
    const n = Number(text);
    if (!Number.isNaN(n)) return n;
  }
  if (typeof originalValue === 'boolean') {
    const lower = text.toLowerCase();
    if (lower === 'true' || lower === '1') return true;
    if (lower === 'false' || lower === '0') return false;
  }
  return text;
}

export function EnumCellEditor({
  initialValue,
  connectionId,
  database,
  schema,
  table,
  column,
  engine,
  source,
  nullable = true,
  fkTarget,
  looseMatches,
  dataType,
  relationshipPort,
  invalid,
  onCommit,
  onCancel,
  inputClassName = 'block w-full h-6 pl-1 pr-6 text-xs bg-background border border-primary rounded-sm outline-none',
  containerClassName = 'relative w-full',
}: EnumCellEditorProps) {
  const [text, setText] = useState(() => formatForEdit(initialValue));
  // Defer initial open by one tick so the pointer events from the cell's
  // double-click finish processing before the popover content mounts —
  // otherwise Radix can treat the stale pointer-down as an "outside click"
  // and immediately close the popover.
  const [open, setOpen] = useState(false);
  // Armed by the effect below rather than at mount: a lookup column must not
  // fire before its label column resolves (that would cache a keys-only
  // result and hide the labels until a manual refresh).
  const [enabled, setEnabled] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // -1 = no item highlighted yet (Enter commits the typed text instead of an item).
  // ArrowDown moves into the list (0).
  const [highlighted, setHighlighted] = useState(-1);
  // Track whether the user has changed the input. The peek opens with the
  // current cell value pre-selected; we don't want that value to immediately
  // filter the list down to one row — show everything until they type.
  const [userTyped, setUserTyped] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { track } = useAnalytics();

  const { data: metaInfo, isPending: metaInfoPending } = useMetaInfo(connectionId, database);
  /**
   * Explicit declaration or implicit reference-table match — the one seam
   * every lookup consumer resolves through. `fkTarget` is the column's
   * resolved FK target (`resolveCellEditVariant` attaches it whenever a
   * declared-LIKE enum outranked the FK editor), which is exactly what makes
   * implicitness decidable here.
   */
  const effective = useMemo(
    () =>
      resolveEffectiveLookupDeclaration({
        metaInfo,
        schema,
        table,
        column,
        resolvedTarget: fkTarget
          ? {
              toSchema: fkTarget.schema,
              toTable: fkTarget.table,
              toColumn: fkTarget.column,
            }
          : null,
      }),
    [metaInfo, schema, table, column, fkTarget],
  );
  const declared = effective?.kind === 'explicit' ? effective.declaration : undefined;
  /** An implicit column has no declaration — the gear still opens details. */
  const isImplicitLookup = effective?.kind === 'implicit';

  // A lookup enum reads its values (and their names) from the referenced
  // table — the editor learns that from the effective declaration. Every other
  // source keeps today's `'auto'` behaviour untouched.
  const lookupTarget: EnumLookupTarget | undefined =
    effective?.kind === 'implicit'
      ? effective.lookup
      : declared?.source === 'lookup'
        ? declared.lookup
        : undefined;

  // The label column is NOT stored on the declaration — it's the shared, live
  // `tableDisplayColumns` entry for the target table, so a change there takes
  // effect on the next refresh. Costs a `getTableMetadata` call on the TARGET
  // table, so the hook is armed only for a lookup-declared column (no target ⇒
  // the hook stays inert). Same wiring as the filter picker's.
  const labelColumnState = useLookupEnumLabelColumn({
    connectionId,
    database,
    target: lookupTarget,
    metadataEditors: relationshipPort,
  });

  const hook = useEnumValues({
    connectionId,
    database,
    schema,
    table,
    column,
    engine,
    enabled,
    // The editor exists only while it is open, and `enabled` is exactly "both
    // arming gates passed" (MetaInfo read + label column resolved) — so it is
    // also the right signal for the hook's background revalidation of a stale
    // lookup cache (Task 5.1).
    active: enabled,
    ...(lookupTarget
      ? {
          source: 'lookup' as const,
          lookup: {
            toSchema: lookupTarget.toSchema,
            toTable: lookupTarget.toTable,
            toColumn: lookupTarget.toColumn,
            labelColumn: labelColumnState.labelColumn,
          },
        }
      : {}),
  });

  // Arm the fetch as soon as the editor knows what kind of column this is.
  // Two gates: the MetaInfo read (arming before it lands would fire an
  // `'auto'` sample at a lookup column), and — for a lookup column — the
  // target's label column (arming before it lands would cache keys with no
  // labels). Neither gate exists for a plain enum: `metaInfoPending` is false
  // on the first render whenever the grid has already read MetaInfo, which is
  // every path that mounts this editor.
  const lookupLabelPending = !!lookupTarget && !labelColumnState.ready;
  useEffect(() => {
    if (enabled || metaInfoPending || lookupLabelPending) return;
    setEnabled(true);
  }, [enabled, metaInfoPending, lookupLabelPending]);

  // The gates above are invisible: the list keeps showing "Loading values…"
  // from mount until the fetch it is waiting for resolves, exactly as it did
  // when the hook was armed at mount.
  const valuesPending = hook.isLoading || (!enabled && !hook.isCacheHit);

  /**
   * key → label for the option rows. Undefined for every non-lookup source,
   * which keeps their rendering byte-identical to the pre-lookup editor.
   */
  const labelByKey = useMemo(() => {
    if (!hook.entries) return undefined;
    const map = new Map<string, string>();
    for (const entry of hook.entries) {
      if (entry.label !== null && entry.label !== '') map.set(entry.value, entry.label);
    }
    return map.size > 0 ? map : undefined;
  }, [hook.entries]);

  const labelFor = useCallback(
    (key: string): string | null => labelByKey?.get(key) ?? null,
    [labelByKey],
  );

  const filtered = useMemo(() => {
    if (!userTyped) return hook.values;
    const q = text.trim().toLowerCase();
    if (!q) return hook.values;
    // Typing matches the LABEL as well as the stored key — on a lookup column
    // the label is the only part the user recognises.
    return hook.values.filter(
      (v) =>
        v.toLowerCase().includes(q) ||
        (labelByKey?.get(v)?.toLowerCase().includes(q) ?? false),
    );
  }, [userTyped, text, hook.values, labelByKey]);

  const trimmed = text.trim();
  // "Use «typed»" row only when the user actually typed something new — the
  // pre-seeded initialValue isn't an ad-hoc creation candidate.
  const canCreate = userTyped && trimmed.length > 0 && !hook.values.includes(trimmed);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
      // Open the peek AFTER the double-click's pointerdown/up/click sequence
      // is flushed — opening it during the same task lets Radix's outside-
      // click detector swallow the stale events and slam the popover shut.
      setOpen(true);
    });
  }, []);

  // Reset highlight when the filterable list changes (after typing). The first
  // item becomes highlighted *only after* the user has actually filtered the
  // list — before that, leave highlight=-1 so Enter commits the input value.
  useEffect(() => {
    if (userTyped) setHighlighted(0);
  }, [filtered.length, canCreate, userTyped]);

  const commitValue = useCallback(
    /**
     * `fromLookupRow` marks a pick made off a lookup-labelled row (a list row
     * or the row picker) rather than typed text / NULL — the analytics
     * `source` then reports `'lookup'` instead of declared/native.
     */
    (raw: string, fromLookupRow = false) => {
      const parsed = parseEditedValue(raw, initialValue);
      const original = initialValue === undefined ? null : initialValue;
      if (parsed === original || String(parsed) === String(original)) {
        onCancel();
        return;
      }
      track('feature:cell_edit_enum_picked', {
        engine,
        source: fromLookupRow && lookupTarget ? 'lookup' : source,
      });
      onCommit(parsed);
    },
    [initialValue, onCommit, onCancel, track, engine, source, lookupTarget],
  );

  const commitNull = useCallback(() => {
    const original = initialValue === undefined ? null : initialValue;
    if (original === null) {
      onCancel();
      return;
    }
    track('feature:cell_edit_enum_picked', { engine, source });
    onCommit(null);
  }, [initialValue, onCommit, onCancel, track, engine, source]);

  // Navigation layout (top-down):
  //   [0]                 → NULL row, if `nullable`
  //   [nullOffset..]      → filtered enum values
  //   [last]              → "Use «typed»" create row, if `canCreate`
  const nullOffset = nullable ? 1 : 0;
  const totalItems = nullOffset + filtered.length + (canCreate ? 1 : 0);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Ctrl/Cmd+0 → commit NULL on any nullable cell. Stop the browser
      // intercepting it for "zoom reset" while a cell editor is focused.
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        if (!nullable) return;
        e.preventDefault();
        commitNull();
        return;
      }
      if (e.key === 'ArrowDown') {
        if (totalItems === 0) return;
        e.preventDefault();
        setOpen(true);
        setHighlighted((i) => Math.min(i + 1, totalItems - 1));
      } else if (e.key === 'ArrowUp') {
        if (totalItems === 0) return;
        e.preventDefault();
        setHighlighted((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (open && highlighted >= 0 && totalItems > 0) {
          if (nullable && highlighted === 0) {
            commitNull();
            return;
          }
          const valueIndex = highlighted - nullOffset;
          if (valueIndex >= 0 && valueIndex < filtered.length) {
            commitValue(filtered[valueIndex], true);
            return;
          }
          if (canCreate && highlighted === nullOffset + filtered.length) {
            commitValue(trimmed);
            return;
          }
        }
        // Highlight not moved into the list yet → commit the typed/current text.
        commitValue(text);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Unwind one layer at a time: row picker → popover → cancel the edit
        // (same order as `FkCellEditor`). The open dialog normally swallows
        // Esc itself; this keeps the order right if focus ever sits here.
        if (pickerOpen) {
          setPickerOpen(false);
          return;
        }
        if (open) {
          setOpen(false);
          return;
        }
        onCancel();
      }
    },
    [open, pickerOpen, totalItems, highlighted, filtered, canCreate, trimmed, text, commitValue, commitNull, nullable, nullOffset, onCancel],
  );

  const handleRefresh = useCallback(async () => {
    setEnabled(true);
    const result = await hook.refetch();
    if (result?.data) {
      track('feature:enum_values_refresh', {
        engine,
        source: result.data.source,
        // Distinguishes the user pressing Refresh from the background
        // refreshes (`miss` — `useLookupLabelSelfHeal`).
        reason: 'manual',
      });
    }
  }, [engine, hook, track]);

  /** The single FK target the footer's "Search rows in …" row opens. */
  const pickerTarget: FkPickerTarget | null = useMemo(() => {
    if (!fkTarget) return null;
    return {
      schema: fkTarget.schema,
      table: fkTarget.table,
      column: fkTarget.column,
      isLoose: fkTarget.isLoose,
      // The first source-filter-matching loose declaration — carries the
      // `targetFilter` that keeps the picker scoped, and the id that skips
      // the picker's own multi-FK chooser.
      looseRel: looseMatches?.[0],
    };
  }, [fkTarget, looseMatches]);

  const handleOpenRowPicker = useCallback(() => {
    if (!pickerTarget) return;
    // Same event the FK editor fires — it is the same user action, reached
    // from a different editor.
    track('feature:cell_edit_fk_picker_opened', { engine, isLoose: pickerTarget.isLoose });
    setOpen(false);
    setPickerOpen(true);
  }, [pickerTarget, engine, track]);

  const handleRowPicked = useCallback(
    (value: string | number | boolean | Date) => {
      track('feature:cell_edit_fk_picked', { engine, isLoose: pickerTarget?.isLoose ?? false });
      setPickerOpen(false);
      // The editor's normal commit path — the KEY, never the label.
      commitValue(String(value), true);
    },
    [engine, track, pickerTarget, commitValue],
  );

  const handleOpenDetails = useCallback(() => {
    if (!declared && !isImplicitLookup) return;
    openEnumDetailsDialog({
      connectionId,
      database,
      schema,
      table,
      column,
      engine: engine as DatabaseEngineId,
      // An implicit column has no declaration record; `lookup` is the only
      // source it can have (R5 gives the details dialog its implicit variant).
      declaredSource: declared?.source ?? 'lookup',
      source: 'cell-edit-gear',
      // Host metadata provider (Azure Table, S4 review D, D5).
      ...(relationshipPort ? { metadataEditors: relationshipPort } : {}),
    });
  }, [declared, isImplicitLookup, connectionId, database, schema, table, column, engine, relationshipPort]);

  return (
    <div className={containerClassName} data-testid="enum-cell-editor">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <input
            ref={inputRef}
            type="text"
            value={text}
            translate="no"
            onChange={(e) => {
              setText(e.target.value);
              if (!userTyped) setUserTyped(true);
              if (!open) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            className={invalid ? cn(inputClassName, 'border-destructive') : inputClassName}
            {...(invalid ? { 'aria-invalid': true } : {})}
            data-testid="enum-cell-editor-input"
            aria-autocomplete="list"
            aria-expanded={open}
          />
        </PopoverAnchor>
        <PopoverContent
          className="w-64 p-0"
          align="start"
          sideOffset={2}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          data-testid="enum-cell-editor-popover"
        >
          {/* Truncation warning — shared --energy hue (CH-09 §6 item 7). */}
          {hook.truncated && (
            <div
              className="border-b border-energy bg-energy-soft p-2 text-xs"
              data-testid="enum-cell-editor-truncated"
            >
              More than 200 distinct values — this column probably isn’t an enum.
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {nullable && (
              <button
                type="button"
                className={`flex w-full items-center px-2 py-1 text-xs text-left ${
                  highlighted === 0 ? 'bg-accent' : 'hover:bg-accent'
                }`}
                onPointerDown={(e) => {
                  e.preventDefault();
                  commitNull();
                }}
                data-testid="enum-cell-editor-null-option"
                data-highlighted={highlighted === 0 ? 'true' : 'false'}
                title="Ctrl/⌘+0"
              >
                <Check
                  className={`h-3 w-3 mr-2 ${initialValue === null || initialValue === undefined ? 'opacity-100' : 'opacity-0'}`}
                  aria-hidden="true"
                />
                <span className="italic text-muted-foreground">NULL</span>
              </button>
            )}
            {valuesPending ? (
              <div
                className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground"
                data-testid="enum-cell-editor-loading"
              >
                <RandomDotmSpinner size={12} />
                Loading values…
              </div>
            ) : (
              <>
                {filtered.length === 0 && !canCreate && !nullable && (
                  <div className="px-2 py-3 text-xs text-muted-foreground" data-testid="enum-cell-editor-empty">
                    {hook.isError ? 'Failed to load values.' : 'No values found.'}
                  </div>
                )}
                {filtered.map((v, i) => {
                  const isSelected = formatForEdit(initialValue) === v;
                  const itemIndex = i + nullOffset;
                  const isHighlighted = itemIndex === highlighted;
                  // Lookup pairs: the label leads, the stored key stays visible
                  // (muted, right-aligned) so what lands in the cell is never a
                  // mystery. `null` label (or any non-lookup source) ⇒ the bare
                  // key, exactly as before. Same rules as `EnumValuePicker`.
                  const label = labelFor(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      className={`flex w-full items-center px-2 py-1 text-xs text-left ${
                        isHighlighted ? 'bg-accent' : 'hover:bg-accent'
                      }`}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        commitValue(v, true);
                      }}
                      data-testid="enum-cell-editor-item"
                      data-enum-key={v}
                      data-highlighted={isHighlighted ? 'true' : 'false'}
                    >
                      <Check
                        className={`h-3 w-3 mr-2 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                        aria-hidden="true"
                      />
                      <span translate="no" className="truncate">{label ?? v}</span>
                      {label !== null && (
                        <span
                          translate="no"
                          className="ml-auto pl-2 shrink-0 font-mono text-[10px] text-muted-foreground"
                          data-testid="enum-cell-editor-item-key"
                        >
                          {v}
                        </span>
                      )}
                    </button>
                  );
                })}
                {canCreate && (
                  <button
                    type="button"
                    className={`flex w-full items-center px-2 py-1 text-xs text-left ${
                      highlighted === nullOffset + filtered.length ? 'bg-accent' : 'hover:bg-accent'
                    }`}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      commitValue(trimmed);
                    }}
                    data-testid="enum-cell-editor-create"
                  >
                    <Check className="h-3 w-3 mr-2 opacity-0" aria-hidden="true" />
                    <span className="truncate">
                      Use <span translate="no" className="font-medium">“{trimmed}”</span>
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
          <EnumCellEditorFooter
            fkTarget={fkTarget}
            onSearchRows={handleOpenRowPicker}
            onRefresh={() => void handleRefresh()}
            // A cold lookup column would otherwise refresh into a keys-only
            // cache before its label column resolves.
            refreshDisabled={valuesPending || lookupLabelPending}
            refreshSpinning={valuesPending}
            showDetails={!!declared || isImplicitLookup}
            onOpenDetails={handleOpenDetails}
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        title="Show suggestions"
        aria-label="Show suggestions"
        onPointerDown={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
          inputRef.current?.focus();
        }}
        className="absolute inset-y-0 right-0 inline-flex w-5 items-center justify-center text-muted-foreground hover:text-foreground"
        data-testid="enum-cell-editor-chevron"
      >
        <ChevronDown className="h-3 w-3" aria-hidden="true" />
      </button>
      {/* Mounted OUTSIDE the popover (mirrors `FkCellEditor`): opening the row
          picker closes the popover, and a dialog inside it would unmount with
          it. Esc unwinds picker → (already-closed) popover, same order. */}
      {pickerOpen && pickerTarget && (
        <CellFkRowPicker
          target={pickerTarget}
          connectionId={connectionId}
          database={database}
          engine={engine}
          sourceSchema={schema}
          sourceTable={table}
          sourceColumn={column}
          sourceDataType={dataType}
          initialValue={initialValue}
          relationshipPort={relationshipPort}
          onClose={() => setPickerOpen(false)}
          onPicked={handleRowPicked}
          returnFocusRef={inputRef}
          onClearToNull={() => {
            setPickerOpen(false);
            commitNull();
          }}
        />
      )}
    </div>
  );
}
